import type {PosterUiDelegate} from '@PosterWhiteboard/ui/poster-ui.types';
import {registerPosterUiDelegate} from '@PosterWhiteboard/ui/poster-ui';
import {openMessageModal} from '@Modals/message-modal';
import {openErrorModal} from '@Modals/error-modal';
import {openPosterInGenerationWarningModal} from '@Modals/poster-in-generation-warning-modal';
import {openNewDownloadModal} from '@Modals/download-modal/download-modal.library';
import {closePosterEditorMoreOptionsModal} from '@Modals/poster-editor-more-options-modal/poster-editor-more-options-modal';
import {openPosterEditorPageOptionsModal} from '@Modals/poster-editor-page-options-modal/poster-editor-page-options-modal.types';
import {openUserSaveConflictModal} from '@Modals/user-save-conflict-modal';
import {openCropperModal} from '@Modals/cropper-modal/cropper-modal.library';
import {openVideoCropperModal} from '@Modals/video-cropper-modal/video-cropper-modal.types';
import {openReplaceMediaModal} from '@Modals/replace-media-modal';
import {openPosterEditorEditFancyTextModal} from '@Modals/poster-editor-fancy-text-modal';
import {openPosterEditorItemEffectsModal} from '@Modals/poster-editor-item-effects-modal';
import {openPosterEditorMoreOptionsModal} from '@Modals/poster-editor-more-options-modal/poster-editor-more-options-modal';
import {openPosterEditorQRContentModal} from '@Modals/poster-editor-qr-content-modal';
import {openPosterEditorTabsTextModal} from '@Modals/poster-editor-tabs-text-modal';
import {showMessageGrowl, hideMessageGrowl as hideMessageGrowlAction, removeMessageGrowl as removeMessageGrowlAction} from '@Components/message-growl';
import {
  notifyGettyLimitReached,
  onAddItems,
  onCreditsPurchaseSuccess,
  onOpenCropModal,
  onOpenImageBackgroundCropModal,
  onOpenMaskingModal,
  onOpenVideoBackgroundCropModal,
  onOpenVideoCropModal,
  openEditMenuModal,
  openEditScheduleModal,
  openEditTableContentModal,
  openEditTableModal,
  triggerBuyMoreCredits,
} from '@Components/poster-editor/library/poster-editor-open-modals';
import {
  isFabricObjectOnPageCanvas,
  isSinglePageMode,
  moveItemToDifferentPage,
  pasteStylesOnMouseDown,
  removeBgOnMouseDown,
  showEditorNotificationMessageGrowl,
} from '@Components/poster-editor/library/poster-editor-library';
import {scrollToPage} from '@Components/poster-editor/library/poster-editor-page-library';
import {isEmbeddedMorphModeActive} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {showInitalPosterItemsLoadError} from '@Components/inital-poster-items-load-error/inital-poster-items-load-error.reducer';
import {setPosterOverlayLoaderVisibility} from '@Components/poster-editor/poster-editor-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {getCreditsCostForVideoBackgroundRemoval, updateVideoBgRemovalCredits} from '@Components/video-bg-removal-credits-cost/video-bg-removal-credits-cost-library';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {updateImagePopUpState} from '@Components/poster-editor/components/image-item-popup/image-item-popup-slice';
import {updatePopUpStateAndPosition} from '@Components/poster-editor/components/poster-popup-menu/poster-popup-menu-slice';
import {updateSpellCheckPopUpOpenState, updateSpellCheckPopUpTargetAndWords} from '@Components/poster-editor/components/text-item-popup/text-item-popup-slice';
import {setImageBgRemovalCreditsCost} from '@Components/image-bg-removal-credits-cost/image-bg-removal-credits-cost-slice';
import {updateKeyboardShift} from '@Components/modal-container/modal-container-reducer';
import {updateUnlinkedMediaState} from '@Panels/user-media-panel/user-media-panel-reducer';

/**
 * The editor shell's implementation of the poster-whiteboard UI port. Importing this module
 * registers it, so every UI surface that creates a Poster must import this file before
 * poster creation (poster-whiteboard.tsx, download-panel.tsx, publishing-qr-code-panel.tsx).
 */
export const posterUiDelegate: PosterUiDelegate = {
  openMessageModal,
  openErrorModal,
  openPosterInGenerationWarningModal,
  openNewDownloadModal,
  openUserSaveConflictModal,
  showMessageGrowl,
  showEditorNotificationMessageGrowl,
  hideMessageGrowl: (key: string): void => {
    window.PMW.redux.store.dispatch(hideMessageGrowlAction(key));
  },
  removeMessageGrowl: (key: string): void => {
    window.PMW.redux.store.dispatch(removeMessageGrowlAction(key));
  },

  openCropperModal,
  openEditMenuModal,
  openEditScheduleModal,
  openEditTableContentModal,
  openEditTableModal,
  openPosterEditorEditFancyTextModal,
  openPosterEditorItemEffectsModal,
  openPosterEditorMoreOptionsModal,
  closePosterEditorMoreOptionsModal,
  openPosterEditorPageOptionsModal,
  openPosterEditorQRContentModal,
  openPosterEditorTabsTextModal,
  openReplaceMediaModal,
  openVideoCropperModal,
  onOpenCropModal,
  onOpenImageBackgroundCropModal,
  onOpenMaskingModal,
  onOpenVideoBackgroundCropModal,
  onOpenVideoCropModal,

  notifyGettyLimitReached,
  onAddItems,

  getCreditsCostForVideoBackgroundRemoval,
  onCreditsPurchaseSuccess,
  refreshTeamCreditsInStore,
  triggerBuyMoreCredits,
  updateVideoBgRemovalCredits,

  isFabricObjectOnPageCanvas,
  isSinglePageMode,
  isEmbeddedMorphModeActive,
  isPageNavigationBlockedByModal: (): boolean => {
    return window.PMW.redux.store.getState().modals.modalsHashmap[MODALS_IDS.POSTER_EDITOR_PAGE_COLOR_PALETTE_STYLES_MODAL]?.isOpen ?? false;
  },
  moveItemToDifferentPage,
  pasteStylesOnMouseDown,
  removeBgOnMouseDown,
  scrollToPage,

  setImageBgRemovalCreditsCost: (payload: Parameters<typeof setImageBgRemovalCreditsCost>[0]): void => {
    window.PMW.redux.store.dispatch(setImageBgRemovalCreditsCost(payload));
  },
  setPosterOverlayLoaderVisibility: (payload: Parameters<typeof setPosterOverlayLoaderVisibility>[0]): void => {
    window.PMW.redux.store.dispatch(setPosterOverlayLoaderVisibility(payload));
  },
  showInitalPosterItemsLoadError: (): void => {
    window.PMW.redux.store.dispatch(showInitalPosterItemsLoadError());
  },
  updateImagePopUpState: (payload: Parameters<typeof updateImagePopUpState>[0]): void => {
    window.PMW.redux.store.dispatch(updateImagePopUpState(payload));
  },
  updateKeyboardShift: (payload: Parameters<typeof updateKeyboardShift>[0]): void => {
    window.PMW.redux.store.dispatch(updateKeyboardShift(payload));
  },
  updatePopUpStateAndPosition: (payload: Parameters<typeof updatePopUpStateAndPosition>[0]): void => {
    window.PMW.redux.store.dispatch(updatePopUpStateAndPosition(payload));
  },
  updateSpellCheckPopUpOpenState: (payload: Parameters<typeof updateSpellCheckPopUpOpenState>[0]): void => {
    window.PMW.redux.store.dispatch(updateSpellCheckPopUpOpenState(payload));
  },
  updateSpellCheckPopUpTargetAndWords: (payload: Parameters<typeof updateSpellCheckPopUpTargetAndWords>[0]): void => {
    window.PMW.redux.store.dispatch(updateSpellCheckPopUpTargetAndWords(payload));
  },
  updateUnlinkedMediaState: (payload: Parameters<typeof updateUnlinkedMediaState>[0]): void => {
    window.PMW.redux.store.dispatch(updateUnlinkedMediaState(payload));
  },
};

registerPosterUiDelegate(posterUiDelegate);
